import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const chatResponses = {
  greeting: [
    "Hello! Welcome to Park Skill. How can I help you today?",
    "Namaste! I'm here to assist you with information about our skill development programs.",
    "Hi there! Ask me anything about apprenticeship, training, or placement services."
  ],
  apprenticeship: [
    "Park Skill offers apprenticeship programs under both NATS (Ministry of Education) and NAPS (MSDE). We have trained 30,000+ apprentices in the past 9 years. Would you like to know more about eligibility or registration?",
  ],
  nats: [
    "NATS (National Apprenticeship Training Scheme) is under the Ministry of Education. It provides practical training for graduates and diploma holders with monthly stipend and government reimbursement. Contact us at 8504001404 to register your establishment."
  ],
  naps: [
    "NAPS (National Apprenticeship Promotion Scheme) is under MSDE. Benefits include stipend sharing up to Rs 1,500/month and basic training cost support up to Rs 7,500. Visit our Apprenticeship page to register."
  ],
  pmkvy: [
    "PMKVY (Pradhan Mantri Kaushal Vikas Yojana) is a free skill training program. Park Skill has trained 3,000+ candidates under PMKVY STT. It offers NSQF-aligned courses with placement assistance."
  ],
  placement: [
    "We have placed 6,000+ candidates through our skill development programs. We collaborate with 37+ establishments across India. Register on our Careers page to get started!"
  ],
  contact: [
    `You can reach us at:\n📞 Phone: ${companyInfo.phone}\n📧 Email: ${companyInfo.email}\n📍 Address: ${companyInfo.address}\n\nOr visit our Contact page for more options.`
  ],
  registration: [
    "To register as a candidate, visit our Careers page. For establishment registration, visit the Apprenticeship page. Our team will contact you within 24 hours!"
  ],
  documents: [
    "For establishment registration, you'll need: PAN Card, TAN Number, EPFO/ESIC Registration, GST Certificate, Last 2 Years ITR, Official Website/Email ID, and Last 2 Months PF/ESIC Challan Copy."
  ],
  services: [
    "Park Skill offers: 1) Apprenticeship (NATS & NAPS), 2) Skill Development Training, 3) Education Support, 4) Placement Services, 5) Workforce Mobilisation. Which service interests you?"
  ],
  default: [
    "Thank you for your query. For detailed information, please contact us at 8504001404 or email apprenticeship@parkskills.in. You can also visit our Contact page.",
    "I'd be happy to help! Please contact our team at 8504001404 for specific queries, or explore our website for more information."
  ]
};

const getResponse = (message) => {
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('hi') || lowerMessage.includes('hello') || lowerMessage.includes('namaste') || lowerMessage.includes('hey')) {
    return chatResponses.greeting[Math.floor(Math.random() * chatResponses.greeting.length)];
  }
  if (lowerMessage.includes('nats') && !lowerMessage.includes('naps')) {
    return chatResponses.nats[0];
  }
  if (lowerMessage.includes('naps') && !lowerMessage.includes('nats')) {
    return chatResponses.naps[0];
  }
  if (lowerMessage.includes('apprentice') || lowerMessage.includes('training')) {
    return chatResponses.apprenticeship[0];
  }
  if (lowerMessage.includes('pmkvy') || lowerMessage.includes('pradhan mantri')) {
    return chatResponses.pmkvy[0];
  }
  if (lowerMessage.includes('placement') || lowerMessage.includes('job') || lowerMessage.includes('career')) {
    return chatResponses.placement[0];
  }
  if (lowerMessage.includes('contact') || lowerMessage.includes('phone') || lowerMessage.includes('email') || lowerMessage.includes('address')) {
    return chatResponses.contact[0];
  }
  if (lowerMessage.includes('register') || lowerMessage.includes('apply') || lowerMessage.includes('enroll')) {
    return chatResponses.registration[0];
  }
  if (lowerMessage.includes('document') || lowerMessage.includes('require') || lowerMessage.includes('need')) {
    return chatResponses.documents[0];
  }
  if (lowerMessage.includes('service') || lowerMessage.includes('offer') || lowerMessage.includes('provide')) {
    return chatResponses.services[0];
  }
  
  return chatResponses.default[Math.floor(Math.random() * chatResponses.default.length)];
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, type: 'bot', text: 'Hello! Welcome to Park Skill. How can I help you today?', time: new Date() }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: inputValue,
      time: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const response = getResponse(inputValue);
      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: response,
        time: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000 + Math.random() * 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    "What is NATS?",
    "How to register?",
    "Contact details",
    "PMKVY training"
  ];

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-slate-700 rotate-0' : 'bg-blue-700 hover:bg-blue-800'
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Park Skill Assistant</h3>
              <p className="text-blue-200 text-xs">Usually replies instantly</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="h-80 overflow-y-auto p-4 bg-slate-50">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex mb-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-end gap-2 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                  message.type === 'user' ? 'bg-blue-700' : 'bg-emerald-600'
                }`}>
                  {message.type === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>
                <div
                  className={`px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                    message.type === 'user'
                      ? 'bg-blue-700 text-white rounded-br-sm'
                      : 'bg-white text-slate-700 shadow-sm rounded-bl-sm'
                  }`}
                >
                  {message.text}
                </div>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex justify-start mb-3">
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions */}
        {messages.length <= 2 && (
          <div className="px-4 py-2 bg-white border-t flex flex-wrap gap-2">
            {quickQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => {
                  setInputValue(q);
                  setTimeout(() => {
                    handleSend();
                  }, 100);
                }}
                className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-4 bg-white border-t">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-blue-500"
            />
            <Button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              size="icon"
              className="w-10 h-10 rounded-full bg-blue-700 hover:bg-blue-800"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
